import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useDocumentPages } from '../../../hooks/useDocumentPages';
/**
 * Build hierarchical tree from flat block list
 * Groups blocks by type to create visual hierarchy
 */ function buildBlockTree(blocks) {
    const headerBlocks = blocks.filter((b)=>[
            'SectionHeader',
            'Heading',
            'Title'
        ].includes(b.type));
    const contentBlocks = blocks.filter((b)=>[
            'Content',
            'Text',
            'Paragraph'
        ].includes(b.type));
    const tableBlocks = blocks.filter((b)=>b.type === 'Table');
    const listBlocks = blocks.filter((b)=>[
            'List',
            'ListItem'
        ].includes(b.type));
    const otherBlocks = blocks.filter((b)=>![
            'SectionHeader',
            'Heading',
            'Title',
            'Content',
            'Text',
            'Paragraph',
            'Table',
            'List',
            'ListItem'
        ].includes(b.type));
    const rootNodes = [];
    if (headerBlocks.length > 0) {
        rootNodes.push({
            block: {
                id: 'group-headers',
                type: 'Headers',
                content: `${headerBlocks.length} headers`,
                boundingBox: {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0
                },
                confidence: 1
            },
            children: headerBlocks.map((block)=>({
                    block,
                    children: [],
                    level: 1
                })),
            level: 0
        });
    }
    if (contentBlocks.length > 0) {
        rootNodes.push({
            block: {
                id: 'group-content',
                type: 'Content',
                content: `${contentBlocks.length} text blocks`,
                boundingBox: {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0
                },
                confidence: 1
            },
            children: contentBlocks.map((block)=>({
                    block,
                    children: [],
                    level: 1
                })),
            level: 0
        });
    }
    if (tableBlocks.length > 0) {
        rootNodes.push({
            block: {
                id: 'group-tables',
                type: 'Tables',
                content: `${tableBlocks.length} tables`,
                boundingBox: {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0
                },
                confidence: 1
            },
            children: tableBlocks.map((block)=>({
                    block,
                    children: [],
                    level: 1
                })),
            level: 0
        });
    }
    if (listBlocks.length > 0) {
        rootNodes.push({
            block: {
                id: 'group-lists',
                type: 'Lists',
                content: `${listBlocks.length} list items`,
                boundingBox: {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0
                },
                confidence: 1
            },
            children: listBlocks.map((block)=>({
                    block,
                    children: [],
                    level: 1
                })),
            level: 0
        });
    }
    if (otherBlocks.length > 0) {
        rootNodes.push({
            block: {
                id: 'group-other',
                type: 'Other',
                content: `${otherBlocks.length} other blocks`,
                boundingBox: {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0
                },
                confidence: 1
            },
            children: otherBlocks.map((block)=>({
                    block,
                    children: [],
                    level: 1
                })),
            level: 0
        });
    }
    return rootNodes;
}
const TreeNodeItem = ({ node, selectedBlockId, onBlockSelect })=>{
    const [isExpanded, setIsExpanded] = useState(true);
    const isGroup = node.block.id.startsWith('group-');
    const isSelected = node.block.id === selectedBlockId;
    const hasChildren = node.children.length > 0;
    const handleClick = ()=>{
        if (isGroup) {
            setIsExpanded(!isExpanded);
        } else {
            onBlockSelect(isSelected ? null : node.block.id);
        }
    };
    const truncatedContent = node.block.content.length > 50 ? `${node.block.content.slice(0, 50)}...` : node.block.content;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("button", {
                onClick: handleClick,
                className: `w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 text-blue-900' : 'text-gray-700'}`,
                style: {
                    paddingLeft: `${node.level * 12 + 12}px`
                },
                children: [
                    hasChildren && /*#__PURE__*/ _jsx("span", {
                        className: "flex-shrink-0 mt-0.5",
                        children: isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                            className: "w-4 h-4"
                        }) : /*#__PURE__*/ _jsx(ChevronRight, {
                            className: "w-4 h-4"
                        })
                    }),
                    !hasChildren && /*#__PURE__*/ _jsx("span", {
                        className: "w-4"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 min-w-0",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "font-medium",
                                children: node.block.type
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-gray-500 truncate",
                                children: truncatedContent
                            }),
                            !isGroup && /*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-gray-400",
                                children: [
                                    Math.round(node.block.confidence * 100),
                                    "% confidence"
                                ]
                            })
                        ]
                    })
                ]
            }),
            isExpanded && hasChildren && /*#__PURE__*/ _jsx("div", {
                children: node.children.map((child)=>/*#__PURE__*/ _jsx(TreeNodeItem, {
                        node: child,
                        selectedBlockId: selectedBlockId,
                        onBlockSelect: onBlockSelect
                    }, child.block.id))
            })
        ]
    });
};
/**
 * Block Tree Component
 * Left sidebar showing hierarchical view of document blocks
 * Allows navigation and selection of blocks
 */ export const BlockTree = ({ projectId, documentId, pageNumber, selectedBlockId, onBlockSelect })=>{
    const { data: pageData, isLoading } = useDocumentPages(projectId, documentId, pageNumber);
    const tree = useMemo(()=>{
        if (!pageData?.blocks) return [];
        return buildBlockTree(pageData.blocks);
    }, [
        pageData?.blocks
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-4 text-sm text-gray-500",
            children: "Loading blocks..."
        });
    }
    if (!pageData || tree.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-4 text-sm text-gray-500",
            children: "No blocks found on this page"
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        children: tree.map((node)=>/*#__PURE__*/ _jsx(TreeNodeItem, {
                node: node,
                selectedBlockId: selectedBlockId,
                onBlockSelect: onBlockSelect
            }, node.block.id))
    });
};
